import React, { useState } from 'react';
import './FloatingEnquiry.css';
import { CONTACT_INFO } from '../constants/index.js';
import { sendEnquiryEmail } from '../services/emailService.js';

const FloatingEnquiry = ({ isOpen: externalIsOpen, onClose: externalOnClose, packageData, hideFloatingButton }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    packageName: '',
    startDate: '',
    endDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine which state to use: external (package enquiry) takes precedence, then internal
  const isOpen = externalIsOpen || internalIsOpen;
  const isExternallyControlled = externalIsOpen !== undefined && externalIsOpen;

  // Update form when package data is provided
  React.useEffect(() => {
    if (packageData && packageData.title) {
      setEnquiryData(prev => ({
        ...prev,
        packageName: packageData.title,
        message: `I'm interested in the ${packageData.title} package. Please provide me with more details as per the following requirements: `
      }));
    }
  }, [packageData]);

  // Reset form when external enquiry closes and no package data
  React.useEffect(() => {
    if (externalIsOpen === false && !packageData) {
      setEnquiryData({
        name: '',
        email: '',
        phone: '',
        message: '',
        packageName: '',
        startDate: '',
        endDate: ''
      });
    }
  }, [externalIsOpen, packageData]);

  const handleInputChange = (field, value) => {
    setEnquiryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggle = () => {
    if (isExternallyControlled && externalOnClose) {
      // If external enquiry is open, close it
      externalOnClose();
    } else {
      // Toggle internal state for regular floating button usage
      if (!internalIsOpen) {
        // Reset form when opening for regular enquiry (no package data)
        setEnquiryData({
          name: '',
          email: '',
          phone: '',
          message: '',
          packageName: '',
          startDate: '',
          endDate: ''
        });
      }
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const handleCloseEnquiry = () => {
    if (isExternallyControlled && externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  const handlePanelClick = (e) => {
    // Prevent clicks inside the panel from bubbling up and closing the form
    e.stopPropagation();
  };

  const handleEnquiry = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!enquiryData.name.trim() || !enquiryData.email.trim() || !enquiryData.message.trim()) {
      alert('Please fill in all required fields.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enquiryData.email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Phone validation (optional field)
    if (enquiryData.phone.trim()) {
          // eslint-disable-next-line no-useless-escape
    const phoneRegex = /^\+?\d{1,4}[\s-]?\(?\d{1,3}\)?[\s-]?\d{1,4}[\s-]?\d{1,4}[\s-]?\d{1,9}$/;
      if (!phoneRegex.test(enquiryData.phone.trim())) {
        alert('Please enter a valid phone number.');
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      // Send email using EmailJS
      const result = await sendEnquiryEmail(enquiryData);
      
      if (result.success) {
        alert('Thank you for your enquiry! We will get back to you soon.');
        // Reset form
        setEnquiryData({ 
          name: '', 
          email: '', 
          phone: '', 
          message: '',
          packageName: packageData ? packageData.title : '',
          startDate: '',
          endDate: ''
        });
        handleCloseEnquiry();
      } else {
        alert('Sorry, there was an error sending your enquiry. Please try again or call us directly.');
        console.error('Email sending failed:', result.error);
      }
    } catch (error) {
      alert('Sorry, there was an error sending your enquiry. Please try again or call us directly.');
      console.error('Email sending error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`floating-enquiry ${isOpen ? 'open' : 'closed'}`}>
      {/* Backdrop */}
      {isOpen && <div className="backdrop" onClick={handleToggle}></div>}
      
      {/* Floating Button - Hide when package details is open */}
      {!hideFloatingButton && (
        <button 
          className="floating-btn" 
          onClick={handleToggle}
          aria-label={isOpen ? 'Close enquiry form' : 'Open enquiry form'}
        >
          {isOpen ? (
            <i className="fas fa-times"></i>
          ) : (
            <i className="fas fa-envelope"></i>
          )}
          {!isOpen && <span className="btn-text">Enquiry</span>}
        </button>
      )}

      {/* Enquiry Form Panel */}
      <div className={`enquiry-panel ${isOpen ? 'visible' : 'hidden'}`} onClick={handlePanelClick}>
        <div className="panel-header">
          <h3>{packageData?.isDetailed ? 'Detailed Enquiry' : 'Quick Enquiry'}</h3>
          <p>Tell us about your travel requirements</p>
        </div>
        
        <form className="floating-enquiry-form" onSubmit={handleEnquiry}>
          {packageData && (
            <input 
              type="text" 
              placeholder="Package Name"
              value={enquiryData.packageName}
              onChange={(e) => handleInputChange('packageName', e.target.value)}
              disabled={isSubmitting}
              readOnly
              className="package-field"
            />
          )}
          <input 
            type="text" 
            placeholder="Your Name"
            value={enquiryData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            disabled={isSubmitting}
            autoComplete="name"
          />
          <input 
            type="email" 
            placeholder="Your Email"
            value={enquiryData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            disabled={isSubmitting}
            autoComplete="email"
          />
          <input 
            type="tel" 
            placeholder="Your Contact Number"
            value={enquiryData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            disabled={isSubmitting}
            autoComplete="tel"
          />
          {packageData && (
            <div className="date-fields-container">
              <div className="date-field-wrapper">
                <label className="date-label">Start</label>
                <input 
                  type="date" 
                  value={enquiryData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  disabled={isSubmitting}
                  className="date-field from-date"
                  lang="en-GB"
                />
              </div>
              <div className="date-field-wrapper">
                <label className="date-label">End</label>
                <input 
                  type="date" 
                  value={enquiryData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  disabled={isSubmitting}
                  className="date-field to-date"
                  lang="en-GB"
                />
              </div>
            </div>
          )}
          <textarea 
            placeholder={packageData ? "Additional requirements and special requests" : "Your requirements with approx dates and destinations"}
            value={enquiryData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            required
            disabled={isSubmitting}
            rows="3"
            autoComplete="off"
          ></textarea>  
          <button type="submit" disabled={isSubmitting} className="submit-btn">
            <i className="fas fa-paper-plane"></i> 
            {isSubmitting ? 'Sending...' : 'Send Enquiry'}
          </button>
        </form>

        <div className="contact-info">
          <div className="contact-item">
            <a href={`tel:+91${CONTACT_INFO.PHONE.replace(/\s/g, '')}`} className="contact-phone-link">
              <i className="fas fa-phone"></i>
              <span>+91 {CONTACT_INFO.PHONE}</span>
            </a>
          </div>
          <div className="contact-item">
            <i className="fas fa-envelope"></i>
            <span>{CONTACT_INFO.EMAIL}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingEnquiry;